import { useTranslation } from 'react-i18next';
import type { IWorkforceSummary } from '@interfaces/ISubcontractorPortal';
import styles from './WorkforceTable.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

function perfTier(p: number): 'high' | 'mid' | 'low' {
  if (p >= 85) return 'high';
  if (p >= 70) return 'mid';
  return 'low';
}

export function WorkforceTable({
  workforce,
  contractorName,
}: {
  workforce: IWorkforceSummary;
  contractorName: string;
}) {
  const { t } = useTranslation();

  return (
    <section className={styles.panel}>
      <header className={styles.head}>
        <div className={styles.headLeft}>
          <h3 className={styles.title}>
            {t('subcontractors.workforce.title', { name: contractorName })}
          </h3>
          <p className={styles.subtitle}>{t('subcontractors.workforce.subtitle')}</p>
        </div>
        <dl className={styles.summary}>
          <div className={styles.summaryItem}>
            <dt className={styles.summaryLabel}>
              {t('subcontractors.workforce.summary.workers')}
            </dt>
            <dd className={styles.summaryValue}>{workforce.count}</dd>
          </div>
          <div className={styles.summaryItem}>
            <dt className={styles.summaryLabel}>
              {t('subcontractors.workforce.summary.avgPerformance')}
            </dt>
            <dd className={styles.summaryValue}>{workforce.averagePerformance}%</dd>
          </div>
          <div className={styles.summaryItem}>
            <dt className={styles.summaryLabel}>
              {t('subcontractors.workforce.summary.flagged')}
            </dt>
            <dd className={styles.summaryValue} data-tone="warn">
              {workforce.flaggedCount}
            </dd>
          </div>
        </dl>
      </header>

      <ScrollArea orientation="horizontal">
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{t('subcontractors.workforce.col.worker')}</th>
              <th scope="col">{t('subcontractors.workforce.col.role')}</th>
              <th scope="col">{t('subcontractors.workforce.col.level')}</th>
              <th scope="col" className={styles.numCol}>
                {t('subcontractors.workforce.col.experience')}
              </th>
              <th scope="col" className={styles.perfCol}>
                {t('subcontractors.workforce.col.performance')}
              </th>
            </tr>
          </thead>
          <tbody>
            {workforce.workers.map((w) => (
              <tr key={w.id} data-flagged={w.flagged ? 'true' : undefined}>
                <td>
                  <div className={styles.workerCell}>
                    <span className={styles.avatar} aria-hidden="true">
                      {w.initials}
                    </span>
                    <span className={styles.workerName}>{w.name}</span>
                  </div>
                </td>
                <td className={styles.muted}>{w.role}</td>
                <td>
                  <span className={styles.levelPill} data-level={w.level.toLowerCase()}>
                    {w.level}
                  </span>
                </td>
                <td className={styles.muted}>
                  {t('subcontractors.workforce.years', { y: w.experienceYears })}
                </td>
                <td>
                  <div className={styles.perfCell}>
                    <span className={styles.perfValue} data-tier={perfTier(w.performance)}>
                      {w.performance}%
                    </span>
                    <span
                      className={styles.perfBar}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={w.performance}
                    >
                      <span
                        className={styles.perfFill}
                        data-tier={perfTier(w.performance)}
                        style={{ width: `${w.performance}%` }}
                      />
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>
    </section>
  );
}
